import { registerSchema } from '@argus-labs/auth-shared/auth'
import { VerifyType } from '@argus-labs/auth-shared/verify'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PageCard } from '@/components/ui/page-card'
import { otpVerification } from '@/lib/otp-verification'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/_auth/edit/email')({
  component: EditEmail,
})

function EditEmail() {
  return (
    <PageCard title="Edit Email" description="Change your email address.">
      <EditEmailForm />
    </PageCard>
  )
}

// Pick email from register params
const editEmailFormSchema = registerSchema.pick({ email: true })
type EditEmailFormParams = z.infer<typeof editEmailFormSchema>

function EditEmailForm() {
  const form = useForm<EditEmailFormParams>({
    resolver: zodResolver(editEmailFormSchema),
    defaultValues: { email: '' },
    mode: 'onChange',
  })
  const navigate = useNavigate()

  const onSubmit = async ({ email }: EditEmailFormParams) => {
    try {
      const { data, error } = await otpVerification.requestEmailChange(email)

      if (data) {
        return navigate({
          to: '/verify',
          search: { email, type: VerifyType.ChangeEmail },
        })
      }

      toast.fromError(error)
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2">
      <Input
        control={form.control}
        type="email"
        name="email"
        label="Email Address"
        required
        autoFocus
        autoComplete="email"
        placeholder="eg: mojo.doe@argus.gg"
      />

      <Button type="submit" flex isDisabled={form.formState.isSubmitting}>
        Update
      </Button>
    </form>
  )
}
